/**
 * @component LaydownLocationGuardComponent
 * 
 * A reusable component that checks if the currently selected location supports laydown space.
 * If the location does not support laydown space, it displays a warning message with a list
 * of available locations that do support it.
 * 
 * This component is used by both LaydownLogFormComponent and LaydownLogTableComponent to ensure
 * users are aware when they need to change locations to access laydown space features.
 * 
 * Features:
 * - Automatically checks current location's laydown availability
 * - Displays location labels in a user-friendly format
 * - Updates when location changes
 * - Provides clear instructions for switching locations
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { SelectedLocationService } from '../../../service/selected-location.service';
import { AssetsDataService } from '../../../service/assets-data.service';
import { SelectedLocation } from '../../../models/selectedLocation.interface';

@Component({
  selector: 'app-laydown-location-guard',
  templateUrl: './laydown-location-guard.component.html',
  styleUrls: ['./laydown-location-guard.component.css']
})
export class LaydownLocationGuardComponent implements OnInit, OnDestroy {
  
  private _selectedLocationService: SelectedLocationService;
  private _assetsDataService: AssetsDataService;
  
  private locationSubscription!: Subscription;
  
  public selectedLocation!: SelectedLocation;
  public isLaydownAvailable: boolean = false;
  public laydownEnabledLocations: Record<string, string> = {};
  public laydownEnabledLocationLabels: string = '';

  constructor(
    selectedLocationService: SelectedLocationService,
    assetsDataService: AssetsDataService
  ) {
    this._selectedLocationService = selectedLocationService;
    this._assetsDataService = assetsDataService;
  }

  ngOnInit(): void {
    // Get initial location state
    this.checkLaydownAvailability();
    
    // Subscribe to location changes
    this.locationSubscription = this._selectedLocationService.selectedLocation$
      .subscribe((location: SelectedLocation) => {
        this.selectedLocation = location;
        this.checkLaydownAvailability();
      });
  }

  ngOnDestroy(): void {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
  }

  /**
   * Checks if laydown space is available at the current location
   * and populates the list of available locations if not.
   */
  private checkLaydownAvailability(): void {
    this.selectedLocation = this._selectedLocationService.getSelectedLocation();
    this.isLaydownAvailable = this.selectedLocation.laydownSpaceAvailable;
    
    if (!this.isLaydownAvailable) {
      // Get the list of locations where laydown is available
      this.laydownEnabledLocations = this._assetsDataService.GetLaydownEnabledLocationsRecord();
      
      // Create a formatted string of location labels
      const labels = Object.values(this.laydownEnabledLocations);
      if (labels.length > 1) {
        const lastLabel = labels.pop();
        this.laydownEnabledLocationLabels = labels.join(', ') + ' or ' + lastLabel;
      } else if (labels.length === 1) {
        this.laydownEnabledLocationLabels = labels[0];
      } else {
        this.laydownEnabledLocationLabels = 'No locations available';
      }
    }
  }
}
